/* eslint-disable react-hooks/rules-of-hooks */
import { useHistory } from 'react-router'
import { useCallback, useRef } from 'react'
import { useParams } from 'react-router'
import { useCoreContext } from '../../RPPCoreContext'
import { url } from '../../../parsing/url'
import { baseUseScopedPathname } from './url'

export type useGoToPathFn<T extends string = string> = (
  name: T,
  params?: object,
  routeType?: 'push' | 'replace',
  search?: Record<string, string>,
  hash?: Record<string, string>,
  state?: Record<string, string>
) => () => void

export type useGoToPath<T extends string = string> = (
  name: T,
  params?: object,
  routeType?: 'push' | 'replace',
  search?: Record<string, string>,
  hash?: Record<string, string>
) => void

export const baseUseGoToPath: useGoToPathFn = (
  name,
  params = {},
  routeType = 'push',
  search = {},
  hash = {},
  state = {}
) => {
  const history = useHistory()

  const routeMethod = history[routeType]
  const pathname = baseUseScopedPathname(name, params)

  const queryString = new URLSearchParams(search).toString()
  const hashString = new URLSearchParams(hash).toString()

  return () => {
    const location: {
      pathname: string
      search?: string
      hash?: string
      state?: Record<string, string>
    } = { pathname }

    if (queryString) {
      location.search = queryString
    }

    if (hashString) {
      location.hash = hashString
    }

    if (state && Object.keys(state).length > 0) {
      location.state = state
    }

    routeMethod(location)
  }
}

/**
 * Returns a function that can be called with dynamic route names and parameters
 * Useful for scenarios where the route is determined outside of the hooks (eg: in callbacks).
 * This version captures all hook values upfront to avoid hook calls in callbacks.
 */
export const baseUseGoToPathDynamic = (): useGoToPath => {
  const history = useHistory()
  const { routesByName } = useCoreContext()
  const paramsRef = useRef<ReturnType<typeof useParams>>(useParams())
  const currentParams = paramsRef.current

  return useCallback(
    (name, params = {}, routeType = 'push', search = {}, hash = {}) => {
      const routeMethod = history[routeType]

      // Get the route pattern and build the path using captured values
      const pattern = routesByName[name as string]
      if (!pattern) {
        console.error(`Route pattern not found for: ${name}`)
        return
      }

      const pathname = url(pattern, {
        ...currentParams,
        ...params,
      })

      const queryString = new URLSearchParams(search).toString()
      const hashString = new URLSearchParams(hash).toString()

      routeMethod({
        pathname,
        search: queryString ? queryString : undefined,
        hash: hashString ? hashString : undefined,
      })
    },
    [history, routesByName, currentParams]
  )
}

export type useCreateHrefFn<T extends string = string> = (
  name: T,
  params?: object,
  search?: Record<string, string>
) => string

export const baseCreateHref: useCreateHrefFn = (name, params = {}, search = {}) => {
  const history = useHistory()

  const pathname = baseUseScopedPathname(name, params)

  const queryString = new URLSearchParams(search).toString()

  return history.createHref({ pathname, search: queryString ? queryString : undefined })
}
